//#region IMPORT
import { useState } from 'react';
import {
  Unstable_Grid2 as Grid,
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { AuthButton, Button } from '../../../../Components/MUIs';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupsIcon from '@mui/icons-material/Groups';
import ExtensionIcon from '@mui/icons-material/Extension';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchText } from '../../../../Reducer/SearchReducer';

//#endregion

// 계층 레벨(사업장명>회사명>부서명>파트)별 라벨/아이콘 - 트리 depth(0~3)와 매칭
const ORG_LEVELS = [
  { field: 'depNm1', label: '사업장', Icon: CorporateFareIcon },
  { field: 'depNm2', label: '회사', Icon: ApartmentIcon },
  { field: 'depNm3', label: '부서', Icon: GroupsIcon },
  { field: 'depNm4', label: '파트', Icon: ExtensionIcon },
];

const ACCENT = '#B5651D';

/**
 * 평평한 rowData(depNm1~4)를 사업장>회사>부서>파트 트리로 변환.
 *
 * 상위 단계만 담긴 행(예: 사업장만 있는 행)이 DB에 실제로 없어도(예: 예전에
 * 전체 경로를 한 번에 입력해 등록된 경우) 경로 텍스트 자체를 기준으로 트리를
 * 합성한다 - 특정 depth의 실제 행이 있으면 그 노드에 매달아 편집/삭제 가능하게,
 * 없으면 라벨만 있는 그룹 노드로 표시한다.
 */
function BuildOrgTree(rows) {
  const root = { children: [], childMap: new Map() };

  (rows || []).forEach((row) => {
    const path = [row.depNm1, row.depNm2, row.depNm3, row.depNm4].filter(
      Boolean
    );
    if (path.length === 0) return;

    let cursor = root;
    path.forEach((segment, idx) => {
      let child = cursor.childMap.get(segment);
      if (!child) {
        child = {
          label: segment,
          path: path.slice(0, idx + 1),
          children: [],
          childMap: new Map(),
          row: null, // 이 경로 그대로인 실제 행이 있으면 아래에서 채워짐
        };
        cursor.childMap.set(segment, child);
        cursor.children.push(child);
      }
      if (idx === path.length - 1) {
        child.row = row;
      }
      cursor = child;
    });
  });

  return root.children;
}

function OrgTreeNode({ node, level, onSelect, onDelete, selectedDepCd }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const { label, Icon } = ORG_LEVELS[level] || ORG_LEVELS[ORG_LEVELS.length - 1];
  // row가 없는 노드는 실제 DB 행이 없는 "합성 그룹" 노드 - 편집/삭제 대상 아님
  const hasRow = !!node.row;
  const isSelected = hasRow && !!selectedDepCd && selectedDepCd === node.row.depCd;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={() => hasRow && onSelect(node.row)}
        sx={{
          ml: level * 3,
          py: 0.75,
          pr: 1,
          pl: 0.5,
          borderRadius: 1.5,
          cursor: hasRow ? 'pointer' : 'default',
          bgcolor: isSelected ? 'rgba(181,101,29,0.1)' : 'transparent',
          '&:hover': { bgcolor: isSelected ? 'rgba(181,101,29,0.14)' : hasRow ? 'rgba(0,0,0,0.04)' : 'transparent' },
          '&:hover .org-tree-delete-btn': { opacity: hasRow ? 1 : 0 },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}
        <Icon sx={{ fontSize: 18, color: ACCENT, flexShrink: 0 }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            flexGrow: 1,
            color: hasRow ? 'inherit' : 'text.secondary',
            fontStyle: hasRow ? 'normal' : 'italic',
          }}
        >
          {node.label}
        </Typography>
        <Chip
          label={label}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: '#EEEEEE',
            color: '#616161',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        {hasRow && (
          <Tooltip title="삭제">
            <IconButton
              size="small"
              className="org-tree-delete-btn"
              sx={{ opacity: 0, transition: 'opacity 0.15s', ml: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(node.row);
              }}
            >
              <DeleteOutlineIcon fontSize="small" sx={{ color: '#d32f2f' }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {hasChildren && expanded && (
        <Box>
          {node.children.map((child) => (
            <OrgTreeNode
              key={child.path.join('|')}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              onDelete={onDelete}
              selectedDepCd={selectedDepCd}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function DEP_HAN(props) {
  //#region 변수
  const dispatch = useDispatch();
  const searchInfo = useSelector((state) => state.SearchInfo);
  const { inputs } = props;
  const orgTree = BuildOrgTree(props.rowData);
  const isEditMode = !!inputs.depCd;
  //#endregion

  const HandleFieldChange = (field) => (e) => {
    props.setInputs((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
      <Grid xs={4} md={12}>
        <AuthButton
          menuId="Department"
          saveYn="N"
          printYn="N"
          searchClick={(e) => {
            if (!props.validateSelection()) return;
            dispatch(setSearchText(e));
          }}
          conditions={[
            ...(props.userInfo.authCd === 'ADMIN'
              ? [
                {
                  type: 'combo',
                  name: '회사',
                  items: searchInfo.coList,
                  value: searchInfo.coCd,
                  onChange: async (e) => {
                    await props.handleCoChange(e);
                    await props.SearchLogic1();
                  },
                },
              ]
              : []),
            {
              type: 'combo',
              name: '사업장',
              items: searchInfo.custList,
              value: searchInfo.custCd,
              onChange: async (e) => {
                await props.handleCustChange(e);
                await props.SearchLogic1();
              },
            },
          ]}
          placeholder="검색"
          grids={
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ height: '100%' }}>
              {/* 왼쪽: 등록/수정 폼 */}
              <Box
                sx={{
                  width: { xs: '100%', md: 360 },
                  flexShrink: 0,
                  bgcolor: '#fff',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                  {isEditMode ? '조직 경로 수정' : '신규 조직 경로'}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.5, mb: 3 }}>
                  새 조직 경로를 등록해주세요. 하위 단계까지 입력하면 상위 조직은
                  자동으로 함께 등록됩니다.
                </Typography>

                <Stack spacing={2}>
                  {ORG_LEVELS.map((level, idx) => (
                    <Box key={level.field}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                        {level.label}
                        {idx === 0 && (
                          <Typography component="span" sx={{ color: '#f44336', ml: 0.5 }}>
                            *
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        inputRef={idx === 0 ? props.inputRef : undefined}
                        placeholder={`예) ${level.label}명을 입력해주세요`}
                        value={inputs[level.field] || ''}
                        onChange={HandleFieldChange(level.field)}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  {isEditMode && (
                    <Button
                      name="취소"
                      variant="outlined"
                      fullWidth
                      onClick={props.HandleAddClick}
                    />
                  )}
                  <Button
                    name={isEditMode ? '수정 저장' : '등록'}
                    variant="contained"
                    fullWidth
                    startIcon={!isEditMode ? <AddCircleOutlineIcon /> : undefined}
                    onClick={() => props.SaveLogic1()}
                  />
                </Stack>
              </Box>

              {/* 오른쪽: 읽기 전용 트리 */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  bgcolor: '#fff',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 3,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
                    현재 조직 구조
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="엑셀 업로드">
                      <IconButton
                        size="small"
                        onClick={() => props.fileInputRef?.current?.click()}
                      >
                        <DriveFolderUploadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="엑셀 다운로드">
                      <IconButton size="small" onClick={() => props.ExcelDownload1?.()}>
                        <FileDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  {orgTree.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', p: 2 }}>
                      등록된 조직 구조가 없습니다. 왼쪽 폼에서 새 경로를 등록해주세요.
                    </Typography>
                  ) : (
                    orgTree.map((node) => (
                      <OrgTreeNode
                        key={node.path.join('|')}
                        node={node}
                        level={0}
                        onSelect={props.handleRowSelection}
                        onDelete={props.HandleDeleteNode}
                        selectedDepCd={inputs.depCd}
                      />
                    ))
                  )}
                </Box>
              </Box>

              <input
                type="file"
                ref={props.fileInputRef}
                style={{ display: 'none' }}
                accept=".xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    props.ExcelUpload1?.(file);
                    e.target.value = '';
                  }
                }}
              />
            </Stack>
          }
        />
      </Grid>
    </Grid>
  );
}

export default DEP_HAN;
